import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IAtp } from 'app/models/atp/atp';
import { AtpService } from 'app/services/atp/atp.service';
import { SharedService } from 'app/services/core/shared.service';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { ToastrService } from 'ngx-toastr';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { IAtpFilter } from 'app/models/filters/atp-filter';
import { CommonService } from 'app/services/common/common.service';
import { AtpEditComponent } from './atp-edit/atp-edit.component';
import { AtpApplicationComponent } from './atp-application/atp-application.component';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-atp',
  templateUrl: './atp.component.html',
  styleUrls: ['./atp.component.scss']
})
export class AtpComponent {
  public isMobile: boolean = false;
  public loading: boolean = true;
  public uiData = LEAD_BASED_PAINT;
  public pageSize: number = 50;  
  public trainingProviders: IAtp[] = [];
  public dataSource: MatTableDataSource<any>;    
  public filteredTrainingProviders = [];
  public filterString: string = '';
  public filteredActive = new FormControl('Active');
  public filterDiscipline = new FormControl(null);
  public expirationStartDate = new FormControl(new Date());
  public expirationEndDate = new FormControl(new Date());

  public atpFilter: IAtpFilter = {
    filterString: null,
    filterStatus: null,
    filterType: null,
    expirationStartDate: null,
    expirationEndDate: null,      
  }

  @ViewChild('TablePaginator') paginator: MatPaginator;
  @ViewChild('TableSort') sort: MatSort;
  
  individualApplicationTable = {
    icon: 'recent_actors',
    title:'Individual Applications',
    layout:{
      columns:['atporId','name','inactive','privateCompany','atpAccreditations'],
      container:[
        {displayName:'ATP OR Id',columnName:'atporId', type:'string', size:'10'},              
        {displayName:'ATP Name',columnName:'name', type:'string', size:'25'},                        
        {displayName:'Status',columnName:'inactive', type:'string', size:'10'},
        {displayName:'Visibility',columnName:'privateCompany', type:'string', size:'10'},
        {displayName:'Accreditations',columnName:'atpAccreditations', type:'subtable', size:'45'},     
      ],
      data: []      
    }
  }
  
  subtableColumns = ['accreditationEndorsementType', 'expirationDate', 'actions'];
  subtableDisplayData = [
    {displayName:'Type',columnName:'accreditationEndorsementType', type:'string', size:'40'},
    {displayName:'Expiration',columnName:'expirationDate', type:'date', size:'20'},            
  ];

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public dialog: MatDialog,
    public sharedService: SharedService,
    public commonService: CommonService,
    public atpService: AtpService,    
    public toastr: ToastrService
  ) { 
    this.breakpointObserver.observe([
      Breakpoints.Handset,
      Breakpoints.Tablet,
      Breakpoints.Small,
    ]).subscribe(result => {
      this.isMobile = result.matches;
    });
    this.loadFilters();    
    this.loadATPs();
  }

  loadFilters(){
    this.filterString = this.commonService.atpManagementFilter.filterString;
    this.filteredActive = this.commonService.atpManagementFilter.filteredActive;    
    this.filterDiscipline = this.commonService.atpManagementFilter.filterDiscipline;
    this.expirationStartDate = this.commonService.atpManagementFilter.expirationStartDate;
    this.expirationEndDate = this.commonService.atpManagementFilter.expirationEndDate;

    this.atpFilter.filterString = this.filterString;
    this.atpFilter.filterStatus = this.filteredActive.value;  
    this.atpFilter.filterType = this.filterDiscipline.value;
    this.atpFilter.expirationStartDate = this.expirationStartDate?.value?.toISOString();
    this.atpFilter.expirationEndDate = this.expirationEndDate?.value?.toISOString();  
  }
  
  loadATPs(): void {    
    this.atpService.GetAllUserATPs(this.atpFilter).subscribe(result=>{            
      this.trainingProviders = result.map(atp => {        
        const provider = {
          ...atp,
          atpAccreditations: new MatTableDataSource(atp.accreditations || []),
        };
        return provider;
      });   
      this.filteredTrainingProviders = this.trainingProviders;  
      this.updateDataSource();
      this.loading = false;
    },error=>{this.toastr.error("Error loading ATPs: " + error.message); this.loading = false;}); 
    this.loading = false;   
  }
    
  updateDataSource(){    
    this.dataSource = new MatTableDataSource<any>(this.filteredTrainingProviders);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;    
  }

  editATP(atp: IAtp) {    
    const dialogRef = this.dialog.open(AtpEditComponent, {
      width: '95%',
      data: {atpId: atp?.id ?? 0},});
    dialogRef.afterClosed().subscribe(result => {  
      this.loadATPs();    
    });
  }

  getDisciplineTypes(){
    return ['RRP','LBPA'];
  }

  filterTable(){
    this.commonService.atpManagementFilter.filterString = this.filterString;
    this.commonService.atpManagementFilter.filteredActive = this.filteredActive;
    this.commonService.atpManagementFilter.filterDiscipline = this.filterDiscipline;
    this.commonService.atpManagementFilter.expirationStartDate = this.expirationStartDate;
    this.commonService.atpManagementFilter.expirationEndDate = this.expirationEndDate;

    this.atpFilter.filterString = this.filterString;
    this.atpFilter.filterStatus = this.filteredActive.value;    
    this.atpFilter.filterType = this.filterDiscipline.value;
    this.atpFilter.expirationStartDate = this.expirationStartDate?.value?.toISOString();
    this.atpFilter.expirationEndDate = this.expirationEndDate?.value?.toISOString();
    this.loadATPs();
  }

  getAtpActiveTypes(){
    return ['Active','InActive','Overdue'];
  }

  useReceivedDate(): boolean{
    return this.filteredActive?.value?.includes('Applications');
  }

  clearFilters(){
    this.filterString = '';
    this.filteredActive.setValue('');
    this.filterDiscipline.setValue(null);
    this.expirationStartDate.reset();
    this.expirationEndDate.reset();
    this.filterTable();
  }

  isObjectOrArray(value: any): boolean {
    return value && (Array.isArray(value) || typeof value === 'object');
  }
  
  handleStudentRowClick(row: any, event: any){         
    if(!event.target.className.includes("header")){
      this.editATP(row);
    }
  }

  isRenewalEligible(expirationDate: any): boolean {
    if (!expirationDate) {
      return false;
    }

    const expDate = new Date(expirationDate);
    const today = new Date();
    
    // Calculate the date 6 months from today
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(today.getMonth() + 6);
    
    // Enable renewal if expiration date is within 6 months (past or future)
    // This means: expiration date <= 6 months from now
    return expDate <= sixMonthsFromNow;
  }

  renewAccreditation(atpRow: any, accreditationRow: any): void {
    // Load complete ATP data including staff before opening the application dialog
    this.loading = true;
    
    forkJoin({
      atp: this.atpService.getAtpById(atpRow.id),
      addresses: this.atpService.getAtpAddresses(atpRow.id),
      contactInfo: this.atpService.getAtpContactInformation(atpRow.id),
      staff: this.atpService.getATPPeople(atpRow.id)
    }).subscribe({
      next: (result) => {
        this.loading = false;
        
        // Open ATP application dialog for renewal
        const dialogRef = this.dialog.open(AtpApplicationComponent, this.sharedService.getWizardDialogConfig({
          disableClose: false,
          data: {
            atpId: atpRow.id,
            atp: result.atp,
            atpStaff: result.staff,
            renewalAccreditation: accreditationRow
          }
        }));

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult) {
            this.loadATPs();
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.toastr.error('Error loading ATP data: ' + error.message);
      }
    });
  }

  // Determine if accreditation has a valid (non-cancelled) application
  hasValidApplication(accreditation: any): boolean {
    return accreditation.atpApplicationId && 
           accreditation.atpApplicationId !== 0;
  }

  // View existing application instead of creating renewal
  viewApplication(atpRow: any, accreditationRow: any): void {
    if (!accreditationRow.atpApplicationId || accreditationRow.atpApplicationId === 0) {
      this.toastr.warning('No valid application found');
      return;
    }

    this.loading = true;
    
    forkJoin({
      atp: this.atpService.getAtpById(atpRow.id),
      addresses: this.atpService.getAtpAddresses(atpRow.id),
      contactInfo: this.atpService.getAtpContactInformation(atpRow.id),
      staff: this.atpService.getATPPeople(atpRow.id)
    }).subscribe({
      next: (result) => {
        this.loading = false;
        
        const dialogRef = this.dialog.open(AtpApplicationComponent, this.sharedService.getWizardDialogConfig({
          disableClose: false,
          data: {
            atpApplicationId: accreditationRow.atpApplicationId,
            atpId: atpRow.id,
            atp: result.atp,
            atpStaff: result.staff
          }
        }));

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult) {
            this.loadATPs();
          }
        });
      },
      error: (error) => {
        this.loading = false;
        this.toastr.error('Error loading ATP data: ' + error.message);
      }
    });
  }
}
